import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { OrgPermission } from '../../../../backend/src/organisation/organisation-permission';
import { useUserContext } from '@/Contexts/User';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { SettingsNav, SettingsNavGroup, SettingsNavItem, SettingsWrapper } from '@/new/components/settings-nav';
import { cn } from '@/new/lib/utils';
import { ActivityDashboard } from '@/new/pages/components/activity-dashboard';
import { AuditTab } from '@/new/pages/components/audit-tab';
import { BillingUsage } from '@/new/pages/components/billing-usage';

const sections = [
  { id: 'activity', title: 'Activity', icon: 'lucide--chart-no-axes-combined' },
  { id: 'usage', title: 'Credit usage', icon: 'lucide--chart-column' },
  { id: 'audit', title: 'Audit', icon: 'lucide--clock' },
] as const;

export function NewUsage() {
  const location = useLocation();
  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const { isEnabled } = useOrgPermissions();
  const activeSection = sections.find((section) => location.pathname.includes(`/new-usage/${section.id}`))?.id;
  const canViewUsage = hasPermission(Permission.UsageDashboardView) || hasPermission(Permission.PlatformViewUsage);

  if (!user) return null;
  if (!canViewUsage) return <Navigate to="/matters" replace />;
  if (!isEnabled(OrgPermission.NewUsageDashboard)) return <Navigate to="/matters" replace />;

  const canViewAudit = hasPermission(Permission.AuditView);

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb items={[{ href: '/new-usage', label: 'New Usage', active: true }]} />
      <SettingsWrapper>
        <SettingsNav className="max-w-56">
          <SettingsNavGroup>
            {sections.map((section) => {
              if (section.id === 'audit' && !canViewAudit) return null;
              return (
                <SettingsNavItem key={section.id} asChild active={activeSection === section.id}>
                  <Link to={`/new-usage/${section.id}`}>
                    <Icon className={section.icon} />
                    <span>{section.title}</span>
                  </Link>
                </SettingsNavItem>
              );
            })}
          </SettingsNavGroup>
        </SettingsNav>
        <div
          className={cn(
            'min-h-0 min-w-0 flex-1',
            activeSection === 'audit' ? 'flex flex-col overflow-hidden pt-7' : 'overflow-auto px-8 pt-7 pb-8',
          )}
        >
          <Routes>
            <Route index element={<Navigate to="/new-usage/activity" replace />} />
            <Route path="activity" element={<ActivityDashboard organisationId={user.organisationId} />} />
            <Route path="stats" element={<Navigate to="/new-usage/activity" replace />} />
            <Route path="usage" element={<BillingUsage organisationId={user.organisationId} />} />
            <Route path="billing" element={<Navigate to="/new-usage/usage" replace />} />
            <Route
              path="audit"
              element={
                canViewAudit ? (
                  <AuditTab organisationId={user.organisationId} isViewingOtherOrg={false} settingsLayout />
                ) : (
                  <Navigate to="/new-usage/usage" replace />
                )
              }
            />
          </Routes>
        </div>
      </SettingsWrapper>
    </div>
  );
}
